import styled, { css } from 'styled-components';
import { getColorWithAlpha } from '@elfsight/lib-utils';
import { clamp } from 'es-toolkit';
import { useSlider } from '../../SliderContext';
import { alignToJustifyContent } from '../constants';
import type { PaginationVariantBaseProps } from '../types';
import { Line } from './Line';

const LINE_WIDTH = 52;

export interface LinesProps extends PaginationVariantBaseProps {
  customMargin?: string;
  maxPaginationElementsCount?: number;
  activePaginationElementViewIndex?: number;
}

export function Lines({
  currentIndex,
  slides,
  goTo,
  paginationClassName,
  colors,
  autoplayProgressBar,
  autoplayEnabled,
  autoplayPauseOnHover,
  seconds,
  loop,
  align,
  customMargin,
  maxPaginationElementsCount = 5,
  activePaginationElementViewIndex = 2,
  shouldUseNewAppearance = false
}: LinesProps) {
  const { appClassnamePrefix } = useSlider();

  const { color, active } = colors;

  const linesCount = Math.min(slides.length, maxPaginationElementsCount);
  const innerWidth = linesCount * LINE_WIDTH;
  const lastSlideIndex = slides.length - 1;

  const clampedActivePaginationElementViewIndex = clamp(
    activePaginationElementViewIndex,
    0,
    lastSlideIndex
  );

  const maxInsetMultiplier = clamp(
    slides.length - linesCount,
    0,
    lastSlideIndex
  );

  const currentInsetMultiplier = clamp(
    currentIndex - clampedActivePaginationElementViewIndex,
    0,
    maxInsetMultiplier
  );

  const currentLeftInset = -currentInsetMultiplier * LINE_WIDTH;

  return (
    <LinesComponent
      _appClassPrefix={appClassnamePrefix}
      _customMargin={customMargin}
    >
      <Inner
        _appClassPrefix={appClassnamePrefix}
        _activeColor={active}
        _align={align}
        _defaultColor={color}
        _innerWidth={innerWidth}
        _leftInset={currentLeftInset}
        _paginationClass={paginationClassName}
        _slidesLength={slides.length}
      >
        {slides.map((item, index) => (
          <Line
            key={`${item.key}${index}`}
            currentIndex={currentIndex}
            index={index}
            slidesLength={slides.length}
            paginationClassName={paginationClassName}
            goTo={goTo}
            seconds={seconds}
            autoplayProgressBar={autoplayProgressBar}
            autoplayEnabled={autoplayEnabled}
            autoplayPauseOnHover={autoplayPauseOnHover}
            active={currentIndex === index}
            last={lastSlideIndex === currentIndex && !loop}
            withShadow={shouldUseNewAppearance}
          />
        ))}
      </Inner>
    </LinesComponent>
  );
}

interface LinesComponentProps {
  _appClassPrefix: string;
  _customMargin?: string;
}

const LinesComponent = styled.div.attrs<LinesComponentProps>(
  ({ _appClassPrefix }) => ({
    className: `${_appClassPrefix}-lines-component`
  })
)`
  overflow: hidden;
  display: flex;
  justify-content: center;
  margin: ${({ _customMargin }) => _customMargin || '12px 24px'};
`;

interface InnerProps {
  _appClassPrefix: string;
  _activeColor: string;
  _align: string;
  _defaultColor: string;
  _innerWidth: number;
  _leftInset: number;
  _paginationClass: string;
  _slidesLength: number;
}

const Inner = styled.div.attrs<InnerProps>(({ _appClassPrefix }) => ({
  className: `${_appClassPrefix}-lines-inner`
}))`
  ${({
    _activeColor,
    _align,
    _defaultColor,
    _innerWidth,
    _leftInset,
    _paginationClass,
    _slidesLength
  }) => {
    const justifyValue = alignToJustifyContent(_align);

    return css`
      display: flex;
      margin: 0 -4px;
      overflow: hidden;
      position: relative;

      width: ${_innerWidth}px;

      ${_slidesLength < 5 &&
      justifyValue &&
      `justify-content: ${justifyValue};`}

      & .${_paginationClass}-line {
        background-color: ${_defaultColor};
      }

      & .${_paginationClass}-item-active *,
      &
        .${_paginationClass}-item:not(.${_paginationClass}-item-active):hover
        .${_paginationClass}-line {
        background-color: ${_activeColor};
      }

      & .${_paginationClass}-with-progress .${_paginationClass}-line {
        background-color: ${getColorWithAlpha(_defaultColor, 0.5)};
      }

      & .${_paginationClass}-item {
        left: ${_leftInset}px;
      }
    `;
  }}
`;
